/**
 * Bill detail page orchestrator.
 *
 * @see prototype/purchases/bill-detail.html — document-first detail layout
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Alert, ConfirmDialog, toast } from "@flint/ui";
import { trpc } from "../../../lib/trpc.ts";
import { WEB_ENV } from "../../../lib/env.ts";
import { meQueryOptions } from "../../../lib/session.ts";
import { usePermission } from "../../../lib/permissions.ts";
import type { BillStatus } from "../list/index.ts";
import { RecordPaymentDialog } from "../../vendor-payments/index.ts";
import { BillDetailHeader } from "./bill-detail-header.tsx";
import { BillDetailRibbon } from "./bill-detail-ribbon.tsx";
import { BillDetailFoot } from "./bill-detail-foot.tsx";
import { BillDoc } from "./bill-doc.tsx";
import { BillRail } from "./bill-rail.tsx";
import { BillLinkedStrip } from "./bill-linked-strip.tsx";
import { BillJournalCard } from "./bill-journal-card.tsx";
import { BillTimeline } from "./bill-timeline.tsx";

/** Route-owned bill id for the detail page. */
export interface BillDetailPageProps {
  billId: string;
}

/** Load bill detail data and compose the document-first bill page. */
export function BillDetailPage({ billId }: BillDetailPageProps) {
  const canCreate = usePermission("bills.create");
  const canApprove = usePermission("bills.approve");
  const navigate = useNavigate();
  const api = trpc as typeof trpc & Record<string, any>;
  const utils = api.useUtils() as Record<string, any>;
  const me = useQuery(meQueryOptions);
  const bill = api.bills.get.useQuery({ id: billId });
  const company = api.companies.get.useQuery({});
  const [voidOpen, setVoidOpen] = useState(false);
  const [deleteDraftOpen, setDeleteDraftOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<BillStatus | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Prior payments against this bill — drives the "outstanding" in the
  // composer. Filtered server-side by `billId`; sum is computed locally
  // (V1 lists won't exceed a few rows per bill).
  const paymentsForBill = api.vendorPayments.list.useQuery({ billId, limit: 50 }, { staleTime: 0 });
  const paidSoFarPaise = (
    (paymentsForBill.data?.items ?? []) as Array<{
      grossPaise: string;
      status: string;
    }>
  )
    .filter((p) => p.status === "paid")
    .reduce((acc, p) => acc + BigInt(p.grossPaise), 0n);

  const transition = api.bills.transitionStatus.useMutation({
    onSuccess: async (_data, variables) => {
      await Promise.all([
        utils.bills.get.invalidate({ id: billId }),
        utils.bills.list.invalidate(),
      ]);
      setVoidOpen(false);
      setPendingStatus(null);
      // Status-aware confirmation so the operator can see what just
      // happened without scrolling back to the status pill.
      const copy = transitionToastCopy(variables.status);
      toast.success(copy.title, { description: copy.description });
    },
    onError: (err) => {
      toast.error("Couldn't change status", {
        description: err.message,
      });
    },
  });

  const deleteDraft = api.bills.deleteDraft.useMutation({
    onSuccess: async () => {
      await utils.bills.list.invalidate();
      setDeleteDraftOpen(false);
      toast.success("Draft deleted", {
        description: "The draft bill and its attachments have been removed.",
      });
      void navigate({ to: "/procurement/bills" });
    },
    onError: (err: { message: string }) => {
      toast.error("Couldn't delete draft", { description: err.message });
    },
  });

  if (bill.isLoading || company.isLoading) {
    return <p className="text-sm text-muted">Loading bill...</p>;
  }
  if (bill.error || !bill.data) {
    return (
      <Alert
        intent="bad"
        title="Couldn't load bill"
        description={bill.error?.message ?? "Bill not found."}
      />
    );
  }
  if (company.error || !company.data) {
    return (
      <Alert
        intent="bad"
        title="Couldn't load company profile"
        description={
          company.error?.message ?? "Settings → Company profile is required for bill details."
        }
      />
    );
  }

  const data = bill.data;
  const isFounder = me.data?.company?.role === "Founder";
  const onTransition = (status: BillStatus) => {
    if (status === "void") {
      setPendingStatus(status);
      setVoidOpen(true);
      return;
    }
    transition.mutate({ id: billId, status });
  };

  return (
    <div className="bill-detail-page">
      <BillDetailHeader
        bill={data}
        isFounder={isFounder}
        onEdit={() => navigate({ to: "/procurement/bills/$billId/edit", params: { billId } })}
        onPrint={() => openBillPdf(billId, { download: false })}
        onDownloadPdf={() => openBillPdf(billId, { download: true, docNumber: data.vendorDocNumber })}
        onTransition={onTransition}
        onRecordPayment={() => setPaymentOpen(true)}
        onDeleteDraft={() => setDeleteDraftOpen(true)}
        pending={transition.isPending || deleteDraft.isPending}
        canCreate={canCreate}
        canApprove={canApprove}
      />
      <BillDetailRibbon
        status={data.status}
        totalPaise={data.totalPaise}
        vendorGstin={data.vendorGstin}
        threeWayMatch={data.threeWayMatch}
      />
      {transition.error ? (
        <Alert intent="bad" title="Status change failed" description={transition.error.message} />
      ) : null}

      <div className="doc-stage">
        <BillDoc bill={data} company={company.data} />
        <BillRail
          bill={data}
          isFounder={isFounder}
          onEdit={() => navigate({ to: "/procurement/bills/$billId/edit", params: { billId } })}
          onTransition={onTransition}
          pending={transition.isPending}
        />
      </div>
      <BillLinkedStrip
        vendorDocNumber={data.vendorDocNumber}
        payments={paymentsForBill.data?.items ?? []}
        paymentsLoading={paymentsForBill.isLoading || paymentsForBill.isFetching}
      />
      <BillJournalCard billId={billId} vendorName={data.vendorName} />
      <BillTimeline
        data={{
          createdAt: data.createdAt,
          source: data.source,
          status: data.status,
          threeWayMatch: data.threeWayMatch,
        }}
      />

      {/* Sticky footer — full-width band mirroring the composer-foot
       *  pattern. Status-driven primary actions stay reachable when
       *  the operator scrolls down past the head. */}
      <BillDetailFoot
        bill={{
          status: data.status,
          totalPaise: data.totalPaise,
          vendorName: data.vendorName,
        }}
        isFounder={isFounder}
        onEdit={() => navigate({ to: "/procurement/bills/$billId/edit", params: { billId } })}
        onPrint={() => openBillPdf(billId, { download: false })}
        onTransition={onTransition}
        onRecordPayment={() => setPaymentOpen(true)}
        onDeleteDraft={() => setDeleteDraftOpen(true)}
        pending={transition.isPending || deleteDraft.isPending}
      />

      <ConfirmDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        title="Void this bill?"
        description="Voiding preserves the captured bill as history and prevents further payment actions."
        intent="danger"
        confirmLabel={transition.isPending ? "Voiding..." : "Void bill"}
        onConfirm={() => {
          if (pendingStatus) transition.mutate({ id: billId, status: pendingStatus });
        }}
      />

      <ConfirmDialog
        open={deleteDraftOpen}
        onOpenChange={setDeleteDraftOpen}
        title="Delete this draft?"
        description="The draft bill, its line items, and any attached files will be removed. This can't be undone — but drafts never reach the ledger, so there's nothing to reverse."
        intent="danger"
        confirmLabel={deleteDraft.isPending ? "Deleting…" : "Delete draft"}
        onConfirm={() => deleteDraft.mutate({ id: billId })}
      />

      <RecordPaymentDialog
        open={paymentOpen}
        onOpenChange={setPaymentOpen}
        bill={{
          id: billId,
          vendorId: data.vendorId,
          vendorName: data.vendorName,
          vendorDocNumber: data.vendorDocNumber,
          totalPaise: data.totalPaise,
          paidSoFarPaise: paidSoFarPaise.toString(),
        }}
        onRecorded={async () => {
          await Promise.all([
            utils.bills.get.invalidate({ id: billId }),
            utils.bills.list.invalidate(),
            utils.vendorPayments.list.invalidate({ billId }),
          ]);
          toast.success("Payment recorded", {
            description: "Bill marked as paid (or partially paid) in the ledger.",
          });
        }}
      />
    </div>
  );
}

/**
 * Status-aware toast copy for the `bills.transitionStatus` mutation. Each
 * status the SPA can flip to gets its own title + body so the operator
 * knows what actually happened (vs. a generic "saved" that leaves them
 * checking the status pill).
 */
function transitionToastCopy(status: BillStatus): { title: string; description: string } {
  switch (status) {
    case "approval_pending":
      return {
        title: "Submitted for approval",
        description: "Founder gets a notification to approve and release payment.",
      };
    case "review":
      return {
        title: "Sent back to review",
        description: "The bill is editable again — operator can fix and resubmit.",
      };
    case "ready_to_pay":
      return {
        title: "Approved · ready to pay",
        description: "Founder approved. Record the vendor payment when the money goes out.",
      };
    case "paid":
      return {
        title: "Marked as paid",
        description: "Bill is closed out in the ledger.",
      };
    case "void":
      return {
        title: "Bill voided",
        description: "Voided rows stay in the ledger as historical record.",
      };
    case "draft":
      return {
        title: "Reverted to draft",
        description: "Bill is back in draft and editable.",
      };
    default:
      return {
        title: "Status updated",
        description: `Bill is now ${(status as string).replace(/_/g, " ")}.`,
      };
  }
}

/**
 * Open (or download) the server-rendered bill PDF. Absolute API origin so the
 * request bypasses the Vite SPA fallback and hits Hono. Mirrors the invoice
 * detail's `openInvoicePdf`.
 */
function openBillPdf(billId: string, opts: { download: boolean; docNumber?: string | null }): void {
  const base = `${WEB_ENV.VITE_FLINT_API_URL}/api/bills/${billId}/pdf`;
  if (!opts.download) {
    window.open(base, "_blank", "noopener");
    return;
  }
  const a = document.createElement("a");
  a.href = `${base}?download=1`;
  a.download = `${opts.docNumber && opts.docNumber.length > 0 ? `bill-${opts.docNumber}` : "bill"}.pdf`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
